import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { RiCheckLine } from "react-icons/ri";
import { usePathname } from "next/navigation";
import { EnterpriseTag } from "@/components/enterprise-tag";
import { useProgressNavigation } from "@/components/progress-bar";
import { DROPDOWN_SEARCH_THRESHOLD, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSearch, DropdownMenuSeparator } from "@/components/ui/dropdown";
import { enterpriseRouteModule, tenantHasRoute } from "@/config/sitemap";
import { useUser } from "@/contexts/user-provider";
import { useMayHaveMultipleTenants } from "@/hooks/use-may-have-multiple-tenants";
import { useTenantSwitch } from "@/hooks/use-tenant-switch";
import { cx } from "@/lib/tailwind-utils";
import { TenantSelect } from "@/types/tenant";
import { fetchClinicSetupReadiness } from "@/api/clinic-setup";
import { fetchTenant } from "@/api/tenant";
import { useAvailableTenants } from "@/query/tenant";

/**
 * Where a tenant switch lands: the same page in the other tenant, except from
 * pages that don't carry over. A page the other tenant doesn't have goes to its
 * home page. Onboarding goes to the other tenant's home page,
 * whose guard sends the person back into onboarding if they still owe it there.
 * Clinic setup stays in clinic setup only while the other clinic is itself
 * still being set up; otherwise it too lands on that clinic's home page.
 *
 * Runs after the session has moved, so the other clinic's readiness is readable.
 */
export async function getTenantSwitchPath(pathname: string, target: TenantSelect): Promise<string> {
  const pathParts = pathname.split("/");
  const subDomain = target.subDomain;
  const home = `/${subDomain}${target.homePage || "/dashboard"}`;
  const route = `/${pathParts.slice(2).join("/")}`.split("?")[0];
  const enabledModules = target.kind === "MASTER" && enterpriseRouteModule(route) ? (await fetchTenant(subDomain))?.enabledModules : undefined;
  if (!tenantHasRoute({
    ...target,
    enabledModules
  }, route)) return home;
  if (pathParts[2] === "patient") return `/${subDomain}/patient`;
  if (pathParts[2] === "check-in" && pathParts[3] === "appointment-form") {
    return `/${subDomain}/check-in/calendar`;
  }
  if (pathParts[2] === "onboarding") return home;
  if (pathParts[2] === "clinic-setup") {
    // Readiness is admin-only; a refusal means the wizard isn't theirs to finish.
    const readiness = await fetchClinicSetupReadiness(subDomain);
    return readiness?.progress.setupLocked ? `/${subDomain}/clinic-setup` : home;
  }
  return pathname.split("?")[0].replace(/^\/[^/]+/, `/${subDomain}`);
}

/**
 * The profile menu's Switch Tenant: the tenants this person can move to, and a
 * switch that keeps them on the same page in the other tenant. Empty when they
 * have only one tenant.
 */
export function useSwitchTenant() {
  const $ = _c(20);
  const pathname = usePathname();
  const {
    runWithNavigationGuard
  } = useProgressNavigation();
  const {
    currentUser
  } = useUser();
  const mayHaveMultipleTenants = useMayHaveMultipleTenants();
  let t0;
  if ($[0] !== mayHaveMultipleTenants) {
    t0 = {
      enabled: mayHaveMultipleTenants
    };
    $[0] = mayHaveMultipleTenants;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    data: availableTenants
  } = useAvailableTenants(t0);
  const switchTenant = useTenantSwitch();
  let t1;
  if ($[2] !== availableTenants) {
    t1 = availableTenants && availableTenants.length > 1 ? availableTenants : [];
    $[2] = availableTenants;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const tenants = t1;
  let t2;
  if ($[4] !== pathname) {
    t2 = pathname.split("/")[1] ?? "";
    $[4] = pathname;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const currentTenant = t2;
  let t3;
  if ($[6] !== currentTenant || $[7] !== tenants) {
    t3 = tenants.find(t => t.subDomain === currentTenant)?.name;
    $[6] = currentTenant;
    $[7] = tenants;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const currentTenantName = t3;
  let t4;
  if ($[9] !== currentUser?.firebaseUid || $[10] !== pathname || $[11] !== runWithNavigationGuard || $[12] !== switchTenant || $[13] !== tenants) {
    t4 = value => {
      if (!currentUser?.firebaseUid) {
        return;
      }
      const target = tenants.find(t_0 => t_0.subDomain === value);
      if (!target) {
        return;
      }
      runWithNavigationGuard(`/${value}`, () => {
        switchTenant(value, () => getTenantSwitchPath(pathname, target));
      }, false);
    };
    $[9] = currentUser?.firebaseUid;
    $[10] = pathname;
    $[11] = runWithNavigationGuard;
    $[12] = switchTenant;
    $[13] = tenants;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const switchTo = t4;
  let t5;
  if ($[15] !== currentTenant || $[16] !== currentTenantName || $[17] !== switchTo || $[18] !== tenants) {
    t5 = {
      tenants,
      currentTenant,
      currentTenantName,
      switchTo
    };
    $[15] = currentTenant;
    $[16] = currentTenantName;
    $[17] = switchTo;
    $[18] = tenants;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}
const isDecodaTenant = (tenant: TenantSelect) => tenant.subDomain.startsWith("dev") || tenant.subDomain === "billing" || tenant.subDomain === "demo";

/** Tenant choices for a dropdown, with Decoda's own tenants grouped first. */
export function TenantMenuItems(t0) {
  const $ = _c(21);
  const {
    tenants,
    currentTenant,
    onSelect
  } = t0;
  const [query, setQuery] = useState("");
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== currentTenant || $[1] !== onSelect || $[2] !== query || $[3] !== tenants) {
    const needle = query.trim().toLowerCase();
    const matches = needle ? tenants.filter(t => t.name.toLowerCase().includes(needle) || t.subDomain.toLowerCase().includes(needle)) : tenants;
    const devTenants = matches.filter(isDecodaTenant);
    const prodTenants = matches.filter(_temp);
    if ($[9] !== query || $[10] !== tenants.length) {
      t1 = tenants.length > DROPDOWN_SEARCH_THRESHOLD && <DropdownMenuSearch value={query} onChange={setQuery} placeholder="Search tenants" />;
      $[9] = query;
      $[10] = tenants.length;
      $[11] = t1;
    } else {
      t1 = $[11];
    }
    t2 = matches.length === 0 && <div className="px-2 py-2 text-body text-muted-foreground">No tenants found</div>;
    t3 = devTenants.length > 0 && <><DropdownMenuLabel className="py-1">Decoda Tenants</DropdownMenuLabel>{devTenants.map(tenant => <TenantOption key={tenant.subDomain} tenant={tenant} isCurrent={currentTenant === tenant.subDomain} onSelect={onSelect} />)}</>;
    t4 = devTenants.length > 0 && prodTenants.length > 0 && <><DropdownMenuSeparator /><DropdownMenuLabel className="py-1">Customers</DropdownMenuLabel></>;
    let t6;
    if ($[12] !== currentTenant || $[13] !== onSelect) {
      t6 = tenant_0 => <TenantOption key={tenant_0.subDomain} tenant={tenant_0} isCurrent={currentTenant === tenant_0.subDomain} onSelect={onSelect} />;
      $[12] = currentTenant;
      $[13] = onSelect;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    t5 = prodTenants.map(t6);
    $[0] = currentTenant;
    $[1] = onSelect;
    $[2] = query;
    $[3] = tenants;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[15] !== t1 || $[16] !== t2 || $[17] !== t3 || $[18] !== t4 || $[19] !== t5) {
    t6 = <>{t1}{t2}{t3}{t4}{t5}</>;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
}
function _temp(t_0) {
  return !isDecodaTenant(t_0);
}
function TenantOption(t0) {
  const $ = _c(17);
  const {
    tenant,
    isCurrent,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== onSelect || $[1] !== tenant.subDomain) {
    t1 = () => onSelect(tenant.subDomain);
    $[0] = onSelect;
    $[1] = tenant.subDomain;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = isCurrent && "font-medium";
  let t3;
  if ($[3] !== t2) {
    t3 = cx("flex items-center justify-between gap-2", t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== tenant.name) {
    t4 = <span className="flex-1 truncate">{tenant.name}</span>;
    $[5] = tenant.name;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== tenant.kind) {
    t5 = tenant.kind === "MASTER" && <EnterpriseTag />;
    $[7] = tenant.kind;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== isCurrent) {
    t6 = isCurrent && <RiCheckLine className="h-4 w-4 shrink-0 text-foreground" />;
    $[9] = isCurrent;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t1 || $[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <DropdownMenuItem onClick={t1} className={t3}>{t4}{t5}{t6}</DropdownMenuItem>;
    $[11] = t1;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}